import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Link } from "@mui/material";
import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingRunKind,
  DocumentCraftingRunStatus,
} from "../../../Models/documentCrafting";
import { documentCraftingPaths } from "../Common/documentCraftingNavigation";
import { formatDateTime, useUserName } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";
import { RevisionSelection, isRunWorking } from "./detailModel";

interface Props {
  document: DocumentCraftingDocumentDetail;
  selection: RevisionSelection;
  /** `?revision=N` that does not exist (hidden while the document reloads). */
  missingRevision: string | null;
  onOpenDraft: () => void;
  onTransfer?: () => void;
}

/** Hints above the content: owner left the workspace, a run in progress, an
 * open draft, the origin of a duplicate, a revision that does not exist. */
const DocumentNotices: React.FC<Props> = ({
  document,
  selection,
  missingRevision,
  onOpenDraft,
  onTransfer,
}) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const userName = useUserName();
  const notices: React.ReactNode[] = [];

  // Only managers can act on it (SPEC §16.1, §16.3).
  if (document.permissions.can_manage && (!document.owner || !document.owner_in_workspace)) {
    notices.push(
      <Alert
        key="owner"
        severity="warning"
        action={
          onTransfer && (
            <Button color="inherit" size="small" onClick={onTransfer}>
              {t("detail.transfer.title")}
            </Button>
          )
        }
      >
        {document.owner
          ? t("detail.notices.ownerNotInWorkspace", {
              name: userName(document.owner),
            })
          : t("detail.notices.ownerDeleted")}
      </Alert>,
    );
  }

  // The caller's run; drafts and translations show their own state.
  const run = document.active_run;
  const viewingDraft = selection.kind === "draft";
  if (
    run &&
    run.kind !== DocumentCraftingRunKind.TRANSLATE &&
    !viewingDraft &&
    (isRunWorking(run) || run.status === DocumentCraftingRunStatus.AWAITING_ANSWERS)
  ) {
    const awaiting = run.status === DocumentCraftingRunStatus.AWAITING_ANSWERS;
    notices.push(
      <Alert
        key="run"
        severity={awaiting ? "warning" : "info"}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate(documentCraftingPaths.run(run.id))}
          >
            {awaiting
              ? t("detail.notices.answerQuestions")
              : t("detail.notices.openRun")}
          </Button>
        }
      >
        {awaiting
          ? t("detail.notices.awaitingAnswers", { kind: labels.runKind(run.kind) })
          : t("detail.notices.running", {
              kind: labels.runKind(run.kind),
              status: labels.runStatus(run.status),
              progress: run.progress,
            })}
      </Alert>,
    );
  } else if (
    document.my_draft_revision &&
    !viewingDraft &&
    document.revisions.length > 0
  ) {
    notices.push(
      <Alert
        key="draft"
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={onOpenDraft}>
            {t("detail.notices.openDraft")}
          </Button>
        }
      >
        {t("detail.notices.draftAvailable", {
          date: formatDateTime(
            document.my_draft?.last_activity_at ??
              document.my_draft_revision.created_at,
            i18n.language,
          ),
        })}
      </Alert>,
    );
  }

  if (missingRevision) {
    notices.push(
      <Alert key="missing" severity="info">
        {t("detail.notices.revisionMissing", { number: missingRevision })}
      </Alert>,
    );
  }

  if (document.duplicated_from) {
    const source = document.duplicated_from;
    notices.push(
      <Alert key="duplicate" severity="info" icon={false}>
        {t("detail.notices.duplicatedFrom", { number: source.revision_number })}{" "}
        <Link
          component={RouterLink}
          to={documentCraftingPaths.document(source.document_id, source.revision_number)}
        >
          {t("detail.notices.openSource")}
        </Link>
      </Alert>,
    );
  }

  if (!notices.length) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
      {notices}
    </Box>
  );
};

export default DocumentNotices;
